import {
  AQLQ_DOMAIN_LABELS,
  AQLQ_QUESTIONS,
  AQLQ_SOURCE_MISSING_ITEMS,
  getAQLQAnswerLabel,
  type AQLQDomainKey,
  type AQLQResultPayload,
} from '../tests/AQLQTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getAQLQSeverityLabel = (total?: number | null): string => {
  if (typeof total !== 'number') return '—';
  return 'Escore AQLQ (1–7)';
};

export const getAQLQSeverityTone = (total?: number | null): string => {
  if (typeof total !== 'number') return 'bg-slate-100 text-slate-700';
  return 'bg-slate-100 text-slate-700';
};

export const getAQLQScoreColor = (total?: number | null): string => {
  if (typeof total !== 'number') return '#475569';
  return '#0f766e';
};

const getLowestDomain = (
  domainScores: Partial<Record<AQLQDomainKey, { score?: number | null }>> | null | undefined,
): { key: AQLQDomainKey; score: number } | null => {
  const candidates = (Object.keys(AQLQ_DOMAIN_LABELS) as AQLQDomainKey[])
    .map((key) => ({ key, score: domainScores?.[key]?.score }))
    .filter((item): item is { key: AQLQDomainKey; score: number } => typeof item.score === 'number')
    .sort((a, b) => a.score - b.score);

  return candidates[0] ?? null;
};

export const getAQLQInterpretationText = (
  total?: number | null,
  domainScores?: Partial<Record<AQLQDomainKey, { score?: number | null }>> | null,
  sourceMissingItems: readonly number[] = AQLQ_SOURCE_MISSING_ITEMS,
): string => {
  if (typeof total !== 'number') {
    return 'Não há dados suficientes para interpretar o AQLQ.';
  }

  const lowestDomain = getLowestDomain(domainScores);
  const dominantText = lowestDomain
    ? `O menor escore apareceu em ${AQLQ_DOMAIN_LABELS[lowestDomain.key].toLowerCase()} (${lowestDomain.score.toFixed(2)}).`
    : '';
  const sourceNote = sourceMissingItems.length
    ? `O cálculo segue o anexo fornecido, que não apresenta a questão ${sourceMissingItems.join(', ')}.`
    : '';

  return `A média global foi ${total.toFixed(2)}/7; valores maiores representam melhor qualidade de vida relacionada à asma. ${dominantText} Não foi aplicada faixa basal automática de gravidade. ${sourceNote}`.trim();
};

interface AQLQResultRendererProps {
  payload: Partial<AQLQResultPayload> | null | undefined;
}

function DomainCard({
  title,
  score,
  answered,
  tone,
}: {
  title: string;
  score: number | null;
  answered: number;
  tone: string;
}) {
  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">{title}</div>
      <div className={`mt-3 text-4xl font-black ${tone}`}>{score != null ? score.toFixed(2) : '—'}</div>
      <div className="mt-2 text-sm text-slate-500">{answered} item(ns) considerados</div>
    </div>
  );
}

export default function AQLQResultRenderer({ payload }: AQLQResultRendererProps) {
  const total = typeof payload?.total === 'number' ? Math.max(1, Math.min(7, payload.total)) : null;
  const answersByItem =
    payload?.answersByItem && typeof payload.answersByItem === 'object' ? payload.answersByItem : {};
  const domainScores = (payload?.domainScores && typeof payload.domainScores === 'object'
    ? payload.domainScores
    : {}) as Partial<Record<AQLQDomainKey, { score?: number | null; answered?: number }>>;
  const sourceMissingItems = Array.isArray(payload?.sourceMissingItems)
    ? payload.sourceMissingItems.map((item) => Number(item)).filter((item) => Number.isFinite(item))
    : [];
  const interpretation = resolveAssessmentInterpretationText(
    payload,
    getAQLQInterpretationText(total, domainScores, sourceMissingItems),
  );

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="AQLQ total"
        value={total != null ? total.toFixed(2) : '—'}
        valueColor={getAQLQScoreColor(total)}
        valueCaption={typeof total === 'number' ? `${total.toFixed(2)}/7` : 'Pontuação indisponível'}
        classification={getAQLQSeverityLabel(total)}
        supportingText="Quanto maior a pontuação, melhor a qualidade de vida relacionada à asma."
        interpretation={
          <>
            <p>{interpretation}</p>
            {sourceMissingItems.length > 0 ? (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900">
                O material de referência desta aplicação não apresenta a questão {sourceMissingItems.join(', ')}. As médias consideram apenas os itens disponíveis.
              </div>
            ) : null}
          </>
        }
      >
          <div className="grid gap-4 md:grid-cols-2">
            <DomainCard
              title="Sintomas"
              score={typeof domainScores.symptoms?.score === 'number' ? domainScores.symptoms.score : null}
              answered={typeof domainScores.symptoms?.answered === 'number' ? domainScores.symptoms.answered : 0}
              tone="text-rose-500"
            />
            <DomainCard
              title="Função emocional"
              score={typeof domainScores.emotionalFunction?.score === 'number' ? domainScores.emotionalFunction.score : null}
              answered={typeof domainScores.emotionalFunction?.answered === 'number' ? domainScores.emotionalFunction.answered : 0}
              tone="text-fuchsia-600"
            />
            <DomainCard
              title="Limitação de atividades"
              score={typeof domainScores.activityLimitation?.score === 'number' ? domainScores.activityLimitation.score : null}
              answered={typeof domainScores.activityLimitation?.answered === 'number' ? domainScores.activityLimitation.answered : 0}
              tone="text-amber-600"
            />
            <DomainCard
              title="Estímulo ambiental"
              score={typeof domainScores.environmentalStimuli?.score === 'number' ? domainScores.environmentalStimuli.score : null}
              answered={typeof domainScores.environmentalStimuli?.answered === 'number' ? domainScores.environmentalStimuli.answered : 0}
              tone="text-blue-600"
            />
          </div>
      </QuestionnaireResultHero>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Questão</th>
              <th className="px-6 py-4">Domínio</th>
              <th className="px-6 py-4">Resposta selecionada</th>
              <th className="px-6 py-4 text-center">Pontuação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {AQLQ_QUESTIONS.map((question) => {
              const rawScore = answersByItem[String(question.itemNumber)];
              const score = typeof rawScore === 'number' ? rawScore : null;
              return (
                <tr key={`aqlq-item-${question.itemNumber}`} className="hover:bg-slate-50/60">
                  <td className="px-6 py-4 font-medium text-slate-700">{question.itemNumber}. {question.prompt}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex rounded-lg bg-blue-100 px-2.5 py-1 text-[10px] font-black uppercase text-blue-700">
                      {AQLQ_DOMAIN_LABELS[question.domain]}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{getAQLQAnswerLabel(question, score)}</td>
                  <td className="px-6 py-4 text-center font-black text-slate-900">{score ?? '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
